import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Box, Alert, Container, Typography, Button } from "@mui/material";
import { Lock } from "@mui/icons-material";
import { AppHeader } from "./AppHeader";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
  redirectTo?: string;
}

export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  allowedRoles, 
  redirectTo = "/dashboard" 
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    const handleBackToDashboard = () => {
      navigate(redirectTo);
    };

    return (
      <>
        <AppHeader 
          title="Acesso Negado" 
          subtitle="Você não tem permissão para acessar esta página"
          icon={<Lock />}
          showBackButton={false}
        />
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              p: 4, 
              borderRadius: 2,
              textAlign: "center"
            }}
            icon={<Lock sx={{ fontSize: 48 }} />}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              🔒 Acesso Restrito
            </Typography>
            <Typography variant="body1" gutterBottom>
              Esta funcionalidade está disponível apenas para os perfis: <strong>{allowedRoles.join(', ')}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Seu perfil atual: <strong>{user?.role}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Se você precisar de acesso, entre em contato com a administração.
            </Typography>
            <Button
              variant="contained"
              onClick={handleBackToDashboard}
              sx={{
                mt: 2,
                borderRadius: 2,
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: "none"
              }}
            >
              Voltar ao Dashboard
            </Button>
          </Alert>
        </Container>
      </>
    );
  }

  return <>{children}</>;
};